/**
 * src/state/actions/useArtefactActions.js
 *
 * Artefact register action callbacks — ARTA-03 of the Artefacts per-record
 * storage migration (ADR-0009, docs/decisions/0009-artefacts-per-record-
 * storage.md). Persists through the dedicated artefacts-write per-record
 * store instead of the whole-tenant-state blob, so two users saving
 * DIFFERENT artefacts never falsely conflict — same optimistic-
 * update-then-reconcile shape as useRegistryActions.js's saveAsset/deleteAsset.
 *
 * Sub-hook: useArtefactActions({ updateState, tenantId })
 * Returns: saveArtefact, deleteArtefact, createArtefacts — each takes an
 * optional trailing `session` for the in-app activity feed (logActivity),
 * matching saveAsset/deleteAsset's contract.
 */

import { useCallback } from 'react';
import * as artefactApi from '../../api/artefactApi.js';
import { logActivity } from '../../api/activityLogger.js';
import {
  captureRecord,
  restoreRecord,
  beginRecordWrite,
  markRecordCommitted,
} from './optimisticRollback.js';
import { queueNetworkWrite, dropNetworkWrite } from '../networkRetryQueue.js';

export function useArtefactActions({ updateState, tenantId }) {
  const saveArtefact = useCallback(
    async (artefact, session) => {
      let isCreate = false;
      let before = null;
      const fenceKey = `artefact:${artefact.id}`;
      // GH #6256 part 2: a fresh ordinary write here bypasses the network
      // retry queue entirely (it's only ever populated by the catch block
      // below), so a stale queued retry from an EARLIER failed save of this
      // same artefact could still be armed and outlive this one — landing
      // later and silently overwriting whatever this write (or a concurrent
      // sweeper flag flip) lands, with no 409 and no conflict banner, since
      // artefacts-write's save action takes no expected-version. Drop it
      // first, per networkRetryQueue.js's documented caller obligation
      // (mirrors FindingsPage.jsx's deleteFinding).
      dropNetworkWrite(fenceKey);
      const fence = beginRecordWrite(fenceKey);
      updateState((s) => {
        if (!s.artefacts) s.artefacts = [];
        const idx = s.artefacts.findIndex((a) => a.id === artefact.id);
        isCreate = idx < 0;
        before = captureRecord(s.artefacts, artefact.id);
        if (isCreate) s.artefacts = [artefact, ...s.artefacts];
        else s.artefacts[idx] = artefact;
      });

      // Artefacts are stripped from the blob by state-write once the tenant's
      // artefact store row exists (ARTA-04), so a failed artefacts-write is
      // never rescued by the debounced save's retry — the optimistic record
      // would linger in the list until the next reload and then vanish
      // (mirrors #4991's assets fix). Put it back so the error the caller
      // surfaces is the whole story.
      const reconcile = (r) => {
        markRecordCommitted(fenceKey);
        if (!r?.artefact) return;
        updateState((s) => {
          const idx = (s.artefacts || []).findIndex((a) => a.id === r.artefact.id);
          if (idx >= 0) s.artefacts[idx] = r.artefact;
          else s.artefacts = [r.artefact, ...(s.artefacts || [])];
        });
      };

      let result;
      try {
        result = await artefactApi.saveArtefactRecord(artefact, tenantId);
      } catch (e) {
        updateState((s) => {
          s.artefacts = restoreRecord(s.artefacts, artefact.id, before, { fence });
        });
        // A refusal is final: re-sending it changes nothing, so the caller's
        // inline error is the whole story. A write that never reached the
        // server is queued so a landed retry re-inserts the record through
        // the same reconcile path a first-time success uses.
        const queued = queueNetworkWrite({
          key: fenceKey,
          error: e,
          attempt: () => artefactApi.saveArtefactRecord(artefact, tenantId),
          onSuccess: (r) => reconcile(r || { artefact }),
        });
        e.willRetry = queued;
        throw e;
      }
      reconcile(result);

      if (session) {
        logActivity(tenantId, session, {
          module: 'Artefacts',
          actionType: isCreate ? 'created' : 'edited',
          recordId: artefact.id,
          recordLabel: artefact.title,
        });
      }
      return result;
    },
    [updateState, tenantId]
  );

  const deleteArtefact = useCallback(
    async (artefactId, label, session) => {
      let before = null;
      const fenceKey = `artefact:${artefactId}`;
      const fence = beginRecordWrite(fenceKey);
      updateState((s) => {
        before = captureRecord(s.artefacts, artefactId);
        s.artefacts = (s.artefacts || []).filter((a) => a.id !== artefactId);
      });

      try {
        await artefactApi.deleteArtefactRecord(artefactId, tenantId);
      } catch (e) {
        updateState((s) => {
          s.artefacts = restoreRecord(s.artefacts, artefactId, before, { fence });
        });
        throw e;
      }
      markRecordCommitted(fenceKey);

      if (session) {
        logActivity(tenantId, session, {
          module: 'Artefacts',
          actionType: 'deleted',
          recordId: artefactId,
          recordLabel: label || artefactId,
        });
      }
    },
    [updateState, tenantId]
  );

  /** Bulk-create multiple NEW artefacts in one call (e.g. an .eml import). */
  const createArtefacts = useCallback(
    async (artefacts, session) => {
      if (!Array.isArray(artefacts) || artefacts.length === 0) return;
      const fences = artefacts.map((a) => beginRecordWrite(`artefact:${a.id}`));
      updateState((s) => {
        if (!s.artefacts) s.artefacts = [];
        s.artefacts = [...artefacts, ...s.artefacts];
      });

      let result;
      try {
        result = await artefactApi.createArtefactRecords(artefacts, tenantId);
      } catch (e) {
        const ids = new Set(artefacts.map((a) => a.id));
        updateState((s) => {
          s.artefacts = (s.artefacts || []).filter((a) => !ids.has(a.id));
        });
        throw e;
      }
      fences.forEach((fence) => markRecordCommitted(fence.key));
      if (result?.artefacts) {
        updateState((s) => {
          const byId = new Map(result.artefacts.map((a) => [a.id, a]));
          s.artefacts = (s.artefacts || []).map((a) => (byId.has(a.id) ? byId.get(a.id) : a));
        });
      }

      if (session) {
        for (const artefact of artefacts) {
          logActivity(tenantId, session, {
            module: 'Artefacts',
            actionType: 'created',
            recordId: artefact.id,
            recordLabel: artefact.title,
          });
        }
      }
      return result;
    },
    [updateState, tenantId]
  );

  return { saveArtefact, deleteArtefact, createArtefacts };
}
